import { lazy } from "react";

// A tab that was open before a deploy still holds the previous main bundle, and
// that bundle asks for chunk files by their old content-hashed names. The new
// build does not ship those names, so the request 404s and React surfaces a
// bare ChunkLoadError — the user sees a dead screen on a perfectly healthy app.
//
// The fix is to reload once: index.html is served no-store, so the reload picks
// up the current bundle and the same navigation succeeds. The sessionStorage
// flag is what keeps that honest — without it, a chunk that is genuinely
// missing (a bad deploy, a half-uploaded build) would reload forever.
const RELOADED_KEY = "amshrms:chunk-reloaded";

// Cleared on any successful chunk load, so the one retry is available again for
// the *next* deploy rather than being spent for the lifetime of the tab.
function markLoaded() {
  try { sessionStorage.removeItem(RELOADED_KEY); } catch { /* private mode */ }
}

function isChunkLoadError(err) {
  const name = err?.name || "";
  const msg = String(err?.message || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk .* failed/i.test(msg) ||
    /Loading CSS chunk .* failed/i.test(msg) ||
    // Some browsers report the HTML-instead-of-JS case this way.
    /Unexpected token '<'/.test(msg)
  );
}

/**
 * Drop-in replacement for React.lazy that survives a deploy landing mid-session.
 * Anything that is not a chunk-loading failure is rethrown untouched — a real
 * error inside the imported module must not be masked by a page reload.
 */
export default function lazyWithRetry(factory) {
  return lazy(() =>
    factory().then(
      (mod) => { markLoaded(); return mod; },
      (err) => {
        if (!isChunkLoadError(err)) throw err;

        let alreadyReloaded = true;
        try { alreadyReloaded = sessionStorage.getItem(RELOADED_KEY) === "1"; }
        catch { /* no sessionStorage → do not reload, just report */ }

        if (alreadyReloaded) throw err;

        try { sessionStorage.setItem(RELOADED_KEY, "1"); } catch { /* ignore */ }
        window.location.reload();
        // Never settles: the reload tears the page down before React could
        // render anything from this promise.
        return new Promise(() => {});
      },
    ),
  );
}
